import React, { Fragment, useContext, useEffect, useState } from 'react';
import { styled } from '@mui/material/styles';

import { Button, Grid, IconButton, Typography } from '@mui/material';
import InfoContacts from './components/InfoContacts';
import InfoLocation from './components/InfoLocation';
import { Link as RouterLink, Route, useParams } from 'react-router-dom';
import MeetupEdit from '../MeetupList/components/MeetupEdit/MeetupEdit';
import { AppContext } from '@/AppContext';
import { shortenDate } from '../MeetupList/components/MeetupsTable/MeetupsTable';
import Paper from '@mui/material/Paper';
import Divider from '@mui/material/Divider';
import EditIcon from '@mui/icons-material/Edit';
import Fab from '@mui/material/Fab';
import GearIcon from '@mui/icons-material/Settings';
import InfoImages from './components/InfoImages';

const PREFIX = 'EventInfo';

const classes = {
  root: `${PREFIX}-root`,
  cardContainer: `${PREFIX}-cardContainer`,
  tableContainer__title: `${PREFIX}-tableContainer__title`,
  space: `${PREFIX}-space`,
  photo: `${PREFIX}-photo`,
  paper: `${PREFIX}-paper`,
  header: `${PREFIX}-header`,
  body: `${PREFIX}-body`,
  divider1: `${PREFIX}-divider1`,
  label: `${PREFIX}-label`,
  editButton: `${PREFIX}-editButton`,
};

const Root = styled('div')(({ theme }) => ({
  [`&.${classes.root}`]: {
    padding: theme.spacing(3),
  },

  [`& .${classes.cardContainer}`]: {
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2),
    paddingTop: 0,
  },

  [`& .${classes.tableContainer__title}`]: {
    paddingLeft: 25,
    fontSize: 20,
  },

  [`& .${classes.space}`]: {
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
  },

  [`& .${classes.photo}`]: {
    maxWidth: 300,
    maxHeight: 300,
  },

  [`& .${classes.paper}`]: {
    boxShadow: theme.shadows[5],
    // margin: 'auto',
    // marginBottom: theme.spacing(4),
  },

  [`& .${classes.header}`]: {
    padding: theme.spacing(2, 3, 2, 3),
    position: 'relative',
  },

  [`& .${classes.body}`]: {
    padding: theme.spacing(3, 5, 4),
  },

  [`& .${classes.divider1}`]: {},

  [`& .${classes.label}`]: {
    color: 'rgba(55, 71, 79, 0.6)',
    fontWeight: 'bold',
  },

  [`& .${classes.editButton}`]: {
    position: 'absolute',
    right: theme.spacing(3),
    top: theme.spacing(2),
  },
}));

const EventInfo = () => {
  const appContext = useContext(AppContext);

  const { mid: meetupId } = useParams();

  // retrieve users list
  const [refreshCounter, setRefresh] = useState(0);
  const [infoAbout, setInfoAbout] = useState();
  const [infoContact, setInfoContact] = useState();
  const [infoLocation, setInfoLocation] = useState();

  const backAndRefresh = data => {
    appContext.history.push('/m/' + meetupId + '/info');
    if (data) {
      setRefresh(refreshCounter + 1);
    }
  };

  useEffect(() => {
    appContext.meetupApi.getMeetup(meetupId).then(({ data }) => setInfoAbout(data));
    appContext.eventInfoApi.getContacts(meetupId).then(({ data }) => setInfoContact(data));
    appContext.eventInfoApi.getLocation(meetupId).then(({ data }) => setInfoLocation(data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meetupId, refreshCounter]);

  if (!infoAbout || !infoContact || !infoLocation) {
    return null;
  }

  return (
    <Root className={classes.root}>
      <Paper>
        <div className={classes.header}>
          <Typography variant='h4'>Общая информация</Typography>
          <RouterLink to={'/m/' + meetupId + '/info/about'}>
            <IconButton size='small' className={classes.editButton}>
              <EditIcon />
            </IconButton>
          </RouterLink>
        </div>
        <Divider className={classes.divider1} />
        <div className={classes.body}>
          <Grid container spacing={1}>
            <Grid size={4}>
              <Typography className={classes.label}>Название мероприятия:</Typography>
            </Grid>
            <Grid size={8}>
              <Typography style={{ fontWeight: 'bold' }}>{infoAbout.name}</Typography>
            </Grid>
            <Grid size={4}>
              <Typography className={classes.label}>Даты проведения:</Typography>
            </Grid>
            <Grid size={8}>
              <Typography>
                {shortenDate(infoAbout.startTime)} - {shortenDate(infoAbout.endTime)}
              </Typography>
            </Grid>
          </Grid>
        </div>

        <div className={classes.header}>
          <Typography variant='h4'>Локация</Typography>
          <RouterLink to={'/m/' + meetupId + '/info/location'}>
            <IconButton size='small' className={classes.editButton}>
              <EditIcon />
            </IconButton>
          </RouterLink>
        </div>
        <Divider className={classes.divider1} />
        <div className={classes.body}>
          <Grid container spacing={1}>
            <Grid size={4}>
              <Typography className={classes.label}>Место проведения:</Typography>
            </Grid>
            <Grid size={8}>
              <Typography>{infoLocation.name}</Typography>
            </Grid>
            <Grid size={4}>
              <Typography className={classes.label}>Адрес:</Typography>
            </Grid>
            <Grid size={8}>
              <Typography style={{ whiteSpace: 'pre-line' }}>{infoLocation.address}</Typography>
            </Grid>
            <Grid size={4}>
              <Typography className={classes.label}>Как добраться:</Typography>
            </Grid>
            <Grid size={8}>
              <Typography style={{ whiteSpace: 'pre-line' }}>{infoLocation.directions}</Typography>
            </Grid>

            <Grid size={4}>
              <Typography className={classes.label}>Схема проезда:</Typography>
            </Grid>
            <Grid size={8}>
              {infoLocation.directionsImage && (
                <img className={classes.photo} src={'/api/image/' + infoLocation.directionsImage} alt={'схема'} />
              )}
            </Grid>
          </Grid>
        </div>

        <div className={classes.header}>
          <Typography variant='h4'>Об организаторе</Typography>
          <RouterLink to={'/m/' + meetupId + '/info/contact'}>
            <IconButton size='small' className={classes.editButton}>
              <EditIcon />
            </IconButton>
          </RouterLink>
        </div>
        <Divider className={classes.divider1} />
        <div className={classes.body}>
          <Grid container spacing={1}>
            <Grid size={4}>
              <Typography className={classes.label}>Контакты:</Typography>
            </Grid>
            <Grid size={8}>
              <Typography style={{ whiteSpace: 'pre-line' }}>{infoContact.contacts}</Typography>
            </Grid>
          </Grid>
        </div>

        <div className={classes.header}>
          <Typography variant='h4'>Изображения</Typography>
          <RouterLink to={'/m/' + meetupId + '/info/images'}>
            <IconButton size='small' className={classes.editButton}>
              <EditIcon />
            </IconButton>
          </RouterLink>
        </div>
        <Divider className={classes.divider1} />
        <div className={classes.body}>
          <Grid container spacing={1}>
            <Grid size={4}>
              <Typography className={classes.label}>Программа мероприятия:</Typography>
            </Grid>
            <Grid size={8}>
              {infoLocation.timetableImage && (
                <img className={classes.photo} src={'/api/image/' + infoLocation.timetableImage} alt={'расписание'} />
              )}
            </Grid>

            <Grid size={4}>
              <Typography className={classes.label}>Схема залов:</Typography>
            </Grid>
            <Grid size={8}>
              {infoLocation.locationPlanImage && (
                <img className={classes.photo} src={'/api/image/' + infoLocation.locationPlanImage} alt={'схема'} />
              )}
            </Grid>
          </Grid>
        </div>
      </Paper>
      <Route path={'/m/:mid/info/about'} exact>
        {/*<InfoAbout goBack={backAndRefresh} />*/}
        <MeetupEdit goBack={backAndRefresh} />
      </Route>
      <Route path={'/m/:mid/info/location'} exact>
        <InfoLocation goBack={backAndRefresh} />
      </Route>
      <Route path={'/m/:mid/info/contact'} exact>
        <InfoContacts goBack={backAndRefresh} />
      </Route>
      <Route path={'/m/:mid/info/images'} exact>
        <InfoImages goBack={backAndRefresh} />
      </Route>
    </Root>
  );
};

export default EventInfo;
